import React, { useState } from 'react';
import { Plus, AlertCircle } from 'lucide-react';
import { trpc } from '@lib/trpc';
import { LoadingSpinner, Button } from '@atoms';
import { SearchInput } from '@molecules';
import { StoreTable, AdminStoreModal } from '@organisms';
import type { StoreRecord } from '@types';
import toast from 'react-hot-toast';

export const AdminStoresTab: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [selectedStore, setSelectedStore] = useState<StoreRecord | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  // tRPC queries and mutations
  const { 
    data: storesData, 
    isLoading, 
    error, 
    refetch 
  } = trpc.store.getAll.useQuery();

  const deleteStoreMutation = trpc.store.delete.useMutation({
    onSuccess: () => {
      toast.success('Store deleted successfully');
      refetch();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to delete store');
    },
  });

  const stores = storesData?.data || [];

  // Filter stores based on search term
  const filteredStores = stores.filter(store =>
    store.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Transform and sort stores alphabetically by name
  const transformedStores = filteredStores.map(store => ({
    ...store,
    created_at: (store as any).createdAt || '',
    updated_at: (store as any).updatedAt || ''
  }));
  const sortedStores = [...transformedStores].sort((a, b) => a.name.localeCompare(b.name));

  const handleCreateStore = () => {
    setSelectedStore(null);
    setIsEditing(false);
    setShowStoreModal(true);
  };

  const handleEditStore = (store: StoreRecord) => {
    setSelectedStore(store);
    setIsEditing(true);
    setShowStoreModal(true);
  };

  const handleDeleteStore = async (store: StoreRecord) => {
    if (window.confirm(`Are you sure you want to delete the store "${store.name}"?\n\nThis action cannot be undone and may affect invoices assigned to this store.`)) {
      await deleteStoreMutation.mutateAsync({ id: store.id });
    }
  };

  const handleModalClose = () => {
    setShowStoreModal(false);
    setSelectedStore(null);
    setIsEditing(false);
  };

  const handleModalSuccess = () => {
    refetch();
    handleModalClose();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Loading stores..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <AlertCircle className="w-12 h-12 text-error-500 mb-4" />
        <h3 className="text-lg font-semibold text-text-primary mb-2">
          Failed to load stores
        </h3>
        <p className="text-text-secondary mb-4">
          {error.message || 'An unexpected error occurred'}
        </p>
        <Button onClick={() => refetch()} variant="primary">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">Store Management</h2>
          <p className="text-text-secondary mt-1">
            Manage store locations for invoice processing
          </p>
        </div>
        <Button
          onClick={handleCreateStore}
          variant="primary"
          className="flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>Add Store</span>
        </Button>
      </div>

      {/* Search */}
      <SearchInput
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search stores..."
        className="max-w-md"
      />

      {/* Stores Table */}
      <StoreTable
        stores={sortedStores}
        searchTerm={searchTerm}
        onEditStore={handleEditStore}
        onDeleteStore={handleDeleteStore}
        isDeleteDisabled={() => deleteStoreMutation.isPending}
      />

      {/* Summary */}
      <div className="flex justify-between text-sm text-text-secondary">
        <span>
          Showing {sortedStores.length} of {stores.length} stores
        </span>
        <span>
          Total stores: {stores.length}
        </span>
      </div>

      {/* Store Modal */}
      {showStoreModal && (
        <AdminStoreModal
          store={selectedStore}
          isEditing={isEditing}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};
